'use client'

import {
  Body,
  Container,
  Reveal,
  Section,
  SectionHeader,
  Stagger,
} from 'design-studio'
import { DiagramTwinCubeCorner } from '@/components/diagrams/diagram-twin-cube-corner'

export function SectionProblem() {
  return (
    <Section
      id="problem"
      className="relative scroll-mt-19 py-16 backdrop-blur-md sm:pt-24 sm:pb-32 dark:bg-slate-800/10"
    >
      <Container className="relative z-10 max-w-6xl">
        <Stagger className="space-y-10">
          <Reveal>
            <SectionHeader
              pretitle="The fundamental problem"
              pretitleColor="text-teal-500"
              title="Why AI Coding Needs Mathematical Context"
              description="LLM coding agents are powerful code generators, but their
                  reasoning is fundamentally local. As systems become more
                  stateful, integrated, and policy-heavy, the number of
                  meaningful behaviors and edge cases rises quickly — while
                  the amount of context an LLM can reliably reason over does
                  not."
            />
          </Reveal>

          <Reveal className="space-y-8 transform-3d">
            <div className="grid items-start gap-8 lg:grid-cols-[1fr_1.3fr]">
              <div className="space-y-4">
                <Body size="xl" tone="muted">
                  As edges grow, so does the space of behaviours an agent must
                  reason about. Without a formal model, this space is opaque.
                </Body>
                <Body tone="muted" className="leading-normal">
                  The number of meaningful behaviours grows combinatorially with
                  system complexity — making prompt-local reasoning increasingly
                  unreliable.
                  <br /> <br />
                  This matters most in enterprise and regulated environments,
                  where teams need precise reasoning about behavior,
                  assumptions, compliance, and change impact.
                </Body>
                <Body tone="muted" className="leading-normal">
                  The missing piece is not more generated code. It is a way to
                  discover the behavioral model already embedded in the system
                  and use it as a stable reasoning reference.
                </Body>
                <ul className="ml-2 space-y-2">
                  {[
                    'Large, evolving codebases with many interacting components',
                    'Long-lived business logic and policy-heavy behavior',
                    'Auditability, compliance, and controlled change',
                    'High operational, legal, or financial cost of subtle mistakes',
                  ].map((item) => (
                    <li
                      key={item}
                      className="text-text-muted flex items-start gap-2 text-sm"
                    >
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <DiagramTwinCubeCorner />
            </div>
          </Reveal>
        </Stagger>
      </Container>
    </Section>
  )
}
